'use client';

import { ReactNode, useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { ProjectRecord } from '@/lib/projects/project-types';
import { formatRangeNL, formatRelativeTime } from '@/lib/projects/project-utils';
import { getProductGroupLabel, isValidProductGroupKey } from '@/lib/reference-data/product-groups';
import { RowActions } from '@/components/projects/table/RowActions';
import { RowPreview } from '@/components/projects/table/RowPreview';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { IconChevronLeft, IconChevronRight } from '@tabler/icons-react';

const PAGE_SIZE = 20;

interface ProjectTableProps {
  projects: ProjectRecord[];
  onSelect: (project: ProjectRecord) => void;
  isLoading?: boolean;
  emptyState?: ReactNode;
  resultsCount?: number;
}

export function ProjectTable({
  projects,
  onSelect,
  isLoading,
  emptyState,
  resultsCount,
}: ProjectTableProps) {
  const [[page, pagedProjects], setPageState] = useState<[number, ProjectRecord[]]>([1, projects]);

  const currentPage = pagedProjects === projects ? page : 1;
  if (pagedProjects !== projects) {
    setPageState([1, projects]);
  }

  const totalPages = Math.max(1, Math.ceil(projects.length / PAGE_SIZE));
  const paged = projects.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const goToPage = (p: number) => setPageState([p, projects]);

  const handleRowKeyDown = (e: React.KeyboardEvent, project: ProjectRecord) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onSelect(project);
    }
  };

  const renderToolbar = () => (
    <div className="flex items-center justify-between px-4 py-2.5 border-b">
      <span className="text-sm text-muted-foreground">
        {resultsCount !== undefined ? `${resultsCount} results` : `${projects.length} results`}
      </span>
    </div>
  );

  const renderPagination = () => {
    if (totalPages <= 1) return null;
    const pages = getPaginationPages(currentPage, totalPages);
    return (
      <div className="flex items-center justify-between px-4 py-3 border-t">
        <span className="text-xs text-muted-foreground">
          Page {currentPage} of {totalPages}
        </span>
        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8"
            disabled={currentPage === 1}
            onClick={() => goToPage(currentPage - 1)}
            aria-label="Previous page"
          >
            <IconChevronLeft className="h-4 w-4" />
          </Button>
          {pages.map((p, i) =>
            p === '…' ? (
              <span key={`ellipsis-${i}`} className="px-1 text-muted-foreground text-sm">…</span>
            ) : (
              <Button
                key={p}
                variant={p === currentPage ? 'outline' : 'ghost'}
                size="icon"
                className="h-8 w-8 text-xs"
                onClick={() => goToPage(p as number)}
                aria-current={p === currentPage ? 'page' : undefined}
              >
                {p}
              </Button>
            )
          )}
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8"
            disabled={currentPage === totalPages}
            onClick={() => goToPage(currentPage + 1)}
            aria-label="Next page"
          >
            <IconChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>
    );
  };

  const renderTableHeader = () => (
    <TableHeader>
      <TableRow className="hover:bg-transparent">
        <TableHead className="sticky top-0 bg-background z-10 border-b w-[38%] font-bold">Project</TableHead>
        <TableHead className="sticky top-0 bg-background z-10 border-b w-[14%] font-bold">Country</TableHead>
        <TableHead className="sticky top-0 bg-background z-10 border-b w-[18%] text-right font-bold">Duration</TableHead>
        <TableHead className="sticky top-0 bg-background z-10 border-b w-[6%] font-bold">CFP</TableHead>
        <TableHead className="sticky top-0 bg-background z-10 border-b w-[10%] font-bold">Source</TableHead>
        <TableHead className="sticky top-0 bg-background z-10 border-b w-[8%] text-right font-bold">Updated</TableHead>
        <TableHead className="sticky top-0 bg-background z-10 border-b w-[150px]"></TableHead>
      </TableRow>
    </TableHeader>
  );

  if (isLoading) {
    return (
      <div className="border rounded-lg bg-card">
        {renderToolbar()}
        {/* Mobile skeleton */}
        <div className="lg:hidden divide-y">
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="p-4 space-y-2">
              <Skeleton className="h-4 w-3/4" />
              <Skeleton className="h-3 w-1/2" />
              <div className="flex gap-2 pt-1">
                <Skeleton className="h-5 w-16 rounded-full" />
                <Skeleton className="h-5 w-20 rounded-full" />
              </div>
            </div>
          ))}
        </div>
        {/* Desktop skeleton */}
        <div className="hidden lg:block overflow-auto">
          <Table className="table-fixed">
            {renderTableHeader()}
            <TableBody>
              {Array.from({ length: 5 }).map((_, i) => (
                <TableRow key={i} className={cn('border-b border-border/60', i % 2 === 1 ? 'bg-muted/10' : 'bg-background')}>
                  <TableCell className="py-3">
                    <div className="space-y-1.5">
                      <Skeleton className="h-4 w-3/4" />
                      <Skeleton className="h-3 w-1/2" />
                    </div>
                  </TableCell>
                  <TableCell className="py-3"><Skeleton className="h-4 w-16" /></TableCell>
                  <TableCell className="py-3 text-right"><Skeleton className="h-4 w-28 ml-auto" /></TableCell>
                  <TableCell className="py-3"><Skeleton className="h-5 w-10 rounded-full" /></TableCell>
                  <TableCell className="py-3"><Skeleton className="h-5 w-20 rounded-full" /></TableCell>
                  <TableCell className="py-3 text-right"><Skeleton className="h-4 w-12 ml-auto" /></TableCell>
                  <TableCell className="py-3"></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>
    );
  }

  if (projects.length === 0) {
    return (
      <div className="border rounded-lg bg-card">
        {renderToolbar()}
        {emptyState ? (
          <>{emptyState}</>
        ) : (
          <div className="p-12 text-center">
            <p className="text-lg font-medium mb-2">No projects found</p>
            <p className="text-sm text-muted-foreground">
              Try adjusting your search or filter criteria
            </p>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="border rounded-lg bg-card">
      {renderToolbar()}

      {/* Mobile card list */}
      <div className="lg:hidden divide-y">
        {paged.map((project) => (
          <button
            key={project.id}
            type="button"
            className="w-full text-left px-4 py-3 hover:bg-muted/30 focus:bg-muted/30 focus:outline-none transition-colors"
            onClick={() => onSelect(project)}
          >
            <p className="font-medium text-sm leading-snug mb-0.5">{project.projectName}</p>
            <p className="text-xs text-muted-foreground mb-2">
              {project.organizationName} · {project.country.join(', ')}
            </p>
            <div className="flex flex-wrap items-center gap-1.5">
              <Badge
                variant={project.callForProposalLink ? 'default' : 'secondary'}
                className="text-xs"
              >
                {project.callForProposalLink ? 'CFP' : 'No CFP'}
              </Badge>
              <Badge variant="outline" className="text-xs">
                {project.sourceDomain}
              </Badge>
              {project.productGroup && isValidProductGroupKey(project.productGroup) && (
                <span className="text-xs text-muted-foreground">
                  {getProductGroupLabel(project.productGroup)}
                </span>
              )}
              <span className="text-xs text-muted-foreground ml-auto">
                {formatRelativeTime(project.updatedAt)}
              </span>
            </div>
          </button>
        ))}
      </div>

      {/* Desktop table */}
      <div className="hidden lg:block overflow-auto">
        <Table className="table-fixed">
          {renderTableHeader()}
          <TableBody>
            {paged.map((project, index) => (
              <tr
                key={project.id}
                tabIndex={0}
                className={cn(
                  'group cursor-pointer border-b border-border/60',
                  'hover:bg-muted/30 focus:bg-muted/30 focus:outline-none transition-colors',
                  index % 2 === 1 ? 'bg-muted/10' : 'bg-background'
                )}
                onClick={() => onSelect(project)}
                onKeyDown={(e) => handleRowKeyDown(e, project)}
              >
                <TableCell className="py-2.5 align-top">
                  <div className="min-w-0">
                    <p className="font-medium truncate">{project.projectName}</p>
                    <p className="text-xs text-muted-foreground truncate">
                      {project.organizationName} · {project.sector}
                      {project.productGroup && isValidProductGroupKey(project.productGroup) && (
                        <> · {getProductGroupLabel(project.productGroup)}</>
                      )}
                    </p>
                  </div>
                  <RowPreview summary={project.executiveSummary} />
                </TableCell>
                <TableCell className="py-2.5 align-top text-sm">
                  <span className="block truncate" title={project.country.join(', ')}>{project.country.join(', ')}</span>
                </TableCell>
                <TableCell className="py-2.5 align-top text-right tabular-nums text-sm">
                  {formatRangeNL(project.startDate, project.endDate)}
                </TableCell>
                <TableCell className="py-2.5 align-top">
                  <Badge
                    variant={project.callForProposalLink ? 'default' : 'secondary'}
                    className="min-w-[44px] justify-center text-xs"
                  >
                    {project.callForProposalLink ? 'Yes' : 'No'}
                  </Badge>
                </TableCell>
                <TableCell className="py-2.5 align-top">
                  <Badge variant="outline" className="max-w-full truncate text-xs">
                    {project.sourceDomain}
                  </Badge>
                </TableCell>
                <TableCell className="py-2.5 align-top text-right tabular-nums whitespace-nowrap text-sm text-muted-foreground">
                  {formatRelativeTime(project.updatedAt)}
                </TableCell>
                <TableCell className="py-2.5 align-top text-right">
                  <div className="opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 transition-opacity">
                    <RowActions project={project} onOpen={onSelect} />
                  </div>
                </TableCell>
              </tr>
            ))}
          </TableBody>
        </Table>
      </div>
      {renderPagination()}
    </div>
  );
}

function getPaginationPages(current: number, total: number): (number | '…')[] {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const pages: (number | '…')[] = [1];
  if (current > 3) pages.push('…');
  for (let p = Math.max(2, current - 1); p <= Math.min(total - 1, current + 1); p++) pages.push(p);
  if (current < total - 2) pages.push('…');
  pages.push(total);
  return pages;
}
